import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const revealOnIntersect = (el) => {
  const obs = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        el.classList.remove("section-hidden");
        el.classList.add("section-visible");
        obs.disconnect();
      }
    },
    { threshold: 0.12 }
  );
  obs.observe(el);
  return () => obs.disconnect();
};

export function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => (ref.current ? revealOnIntersect(ref.current) : undefined), [ref]);
  return ref;
}

const scrollToFirst = (selectors) => {
  const el = selectors.map((s) => document.querySelector(s)).find(Boolean);
  if (!el) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  el.style.scrollMarginTop = "96px";
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export function useScrollToSuccess(submitted, ...selectors) {
  const selectorKey = selectors.join("|");
  useEffect(() => {
    if (submitted) scrollToFirst(selectorKey.split("|"));
  }, [submitted, selectorKey]);
}

const scrollToHash = (hash) => {
  const timer = setTimeout(() => {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }, 100);
  return () => clearTimeout(timer);
};

export function useHashScroll(hash) {
  useEffect(() => (hash ? scrollToHash(hash) : undefined), [hash]);
}

const PAGE_TITLES = {
  "/": "Cindyrella Cleaning LLC — Naples FL | Licensed & Insured Cleaning Service",
  "/services": "Cleaning Services in Naples FL — Homes, Airbnb, Offices & Commercial | Cindyrella Cleaning",
  "/about": "About Us — Mission, Vision & Values | Cindyrella Cleaning LLC Naples FL",
  "/gallery": "Our Work — Photo & Video Gallery | Cindyrella Cleaning LLC Naples FL",
  "/contact": "Contact Us — Free Cleaning Quote in Naples FL | Cindyrella Cleaning LLC",
  "/estimate": "Free Cleaning Estimate in 60 Seconds | Cindyrella Cleaning LLC Naples FL",
  "/blog": "Cleaning Tips & Insights for Naples FL | Cindyrella Cleaning Blog",
  "/privacy": "Privacy Policy | Cindyrella Cleaning LLC",
  "/terms": "Terms of Service | Cindyrella Cleaning LLC",
  "/non-discrimination": "Equal Opportunity & Non-Discrimination Statement | Cindyrella Cleaning LLC",
  "/accessibility": "Accessibility Statement | Cindyrella Cleaning LLC",
  "/safety": "Safety & Insurance Commitment | Cindyrella Cleaning LLC",
  "/government-contracts": "Capability Statement — Government & Commercial Contracting | Cindyrella Cleaning Services LLC",
  "/es": "Cindyrella Cleaning LLC — Limpieza Profesional en Naples, FL | Con Licencia y Seguro",
  "/es/estimate": "Cotización gratis en 60 segundos | Cindyrella Cleaning LLC Naples FL",
  "/services/deep-cleaning-naples-fl": "Deep Cleaning Naples FL — Licensed & Insured | Cindyrella Cleaning LLC",
  "/services/move-in-move-out-cleaning-naples-fl": "Move In / Move Out Cleaning Naples FL | Cindyrella Cleaning LLC",
  "/services/airbnb-cleaning-naples-fl": "Airbnb Cleaning Service Naples FL — Turnovers | Cindyrella Cleaning LLC",
  "/services/post-construction-cleaning-naples-fl": "Post Construction Cleaning Naples FL | Cindyrella Cleaning",
  "/admin": "Admin Panel | Cindyrella Cleaning LLC",
};

const onRouteChange = (pathname) => {
  window.scrollTo({ top: 0, behavior: "instant" });
  const title = PAGE_TITLES[pathname];
  if (title) document.title = title;
  if (window.gtag) {
    window.gtag("event", "page_view", { page_path: pathname });
  }
};

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => onRouteChange(pathname), [pathname]);
  return null;
}

